import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Save, Key, Clock, Shield, Eye } from 'lucide-react';

/**
 * Set Value Configuration Component
 * 
 * Simple UI for storing values in the state store.
 * Designed to be understandable by a 10-year-old.
 */
const SetValueConfig = ({ config, onChange }) => {
  const [key, setKey] = useState(config?.key || '');
  const [value, setValue] = useState(config?.value || '');
  const [scope, setScope] = useState(config?.scope || 'user');
  const [ttl, setTtl] = useState(config?.ttl || null);
  const [encrypt, setEncrypt] = useState(config?.encrypt || false);

  const updateConfig = (updates) => {
    onChange({
      ...config,
      ...updates
    });
  };

  const scopes = [
    { value: 'user', label: 'My Data', description: 'Only you can access this' },
    { value: 'workspace', label: 'Team Data', description: 'Everyone in your workspace can access' },
    { value: 'execution', label: 'This Run Only', description: 'Temporary data for this workflow run' },
    { value: 'global', label: 'Global Data', description: 'Shared across everyone (admin only)' }
  ];

  const ttlOptions = [
    { value: null, label: 'Forever', description: 'Never expires' },
    { value: 300, label: '5 minutes', description: 'Good for temporary data' },
    { value: 3600, label: '1 hour', description: 'Good for session data' },
    { value: 86400, label: '1 day', description: 'Good for daily data' },
    { value: 604800, label: '1 week', description: 'Good for weekly data' },
    { value: 2592000, label: '30 days', description: 'Good for monthly data' }
  ];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Save className="w-5 h-5 text-green-500" />
          <h3 className="text-lg font-semibold">Save a Value</h3>
        </div>
        <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-500/30">
          Store Data
        </Badge>
      </div>

      {/* Explanation */}
      <Card className="bg-green-500/5 border-green-500/20">
        <CardContent className="pt-4">
          <p className="text-sm text-muted-foreground">
            <strong>What does this do?</strong> This saves a value so you can use it later. 
            Think of it like putting something in a box with a label.
          </p>
        </CardContent>
      </Card>

      {/* Key Input */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Key className="w-4 h-4 text-green-500" />
            <CardTitle className="text-sm font-medium">What's the name of the value?</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          <Input
            placeholder="e.g., customer_count, cart_items, user_preferences"
            value={key}
            onChange={(e) => {
              setKey(e.target.value);
              updateConfig({ key: e.target.value });
            }}
            className="font-mono text-sm"
          />
          <p className="text-xs text-muted-foreground">
            This is like the label on the box. Use a name that describes what you're saving.
          </p>
        </CardContent>
      </Card>

      {/* Value Input */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium">What value do you want to save?</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <Input
            placeholder="e.g., 42, hello world, {{count + 1}}"
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              updateConfig({ value: e.target.value });
            }}
            className="font-mono text-sm"
          />
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">
              You can use:
            </p>
            <ul className="text-xs text-muted-foreground list-disc list-inside space-y-1">
              <li>Direct values: <code className="bg-muted px-1 rounded">42</code>, <code className="bg-muted px-1 rounded">"hello"</code></li>
              <li>Variables: <code className="bg-muted px-1 rounded">{`{{count}}`}</code></li>
              <li>Expressions: <code className="bg-muted px-1 rounded">{`{{count + 1}}`}</code></li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Scope Selector */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium">Where should it be stored?</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Select value={scope} onValueChange={(value) => {
            setScope(value);
            updateConfig({ scope: value });
          }}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {scopes.map((s) => (
                <SelectItem key={s.value} value={s.value}>
                  <div className="flex flex-col">
                    <span className="font-medium">{s.label}</span>
                    <span className="text-xs text-muted-foreground">{s.description}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="p-3 bg-muted rounded-lg">
            <p className="text-xs">
              <strong>Selected:</strong> {scopes.find(s => s.value === scope)?.label}
              <br />
              {scopes.find(s => s.value === scope)?.description}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* TTL Selector */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-orange-500" />
            <CardTitle className="text-sm font-medium">How long should it last?</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <Select value={ttl?.toString() || 'null'} onValueChange={(value) => {
            const ttlValue = value === 'null' ? null : parseInt(value);
            setTtl(ttlValue);
            updateConfig({ ttl: ttlValue });
          }}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ttlOptions.map((option) => (
                <SelectItem key={option.value || 'null'} value={option.value?.toString() || 'null'}>
                  <div className="flex flex-col">
                    <span className="font-medium">{option.label}</span>
                    <span className="text-xs text-muted-foreground">{option.description}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            {ttl ? `The value will automatically delete after ${ttlOptions.find(o => o.value === ttl)?.label}.` : 'The value will never expire.'}
          </p>
        </CardContent>
      </Card>

      {/* Encryption Toggle */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-purple-500" />
            <CardTitle className="text-sm font-medium">Should it be encrypted?</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label htmlFor="encrypt-toggle">Encrypt this value</Label>
              <p className="text-xs text-muted-foreground">
                Turn this on for sensitive data like passwords or API keys
              </p>
            </div>
            <Switch
              id="encrypt-toggle"
              checked={encrypt}
              onCheckedChange={(checked) => {
                setEncrypt(checked);
                updateConfig({ encrypt: checked });
              }}
            />
          </div>
          {encrypt && (
            <div className="p-3 bg-purple-500/10 rounded-lg border border-purple-500/20">
              <p className="text-xs text-purple-600">
                <strong>🔒 Encryption enabled:</strong> This value will be encrypted before saving.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Summary */}
      <Card className="bg-green-500/5 border-green-500/20">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium">Summary</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <p>
            <span className="text-muted-foreground">Save value:</span>{' '}
            <span className="font-mono text-xs bg-muted px-2 py-1 rounded">{value || '(not set)'}</span>
          </p>
          <p>
            <span className="text-muted-foreground">As:</span>{' '}
            <span className="font-mono text-xs bg-muted px-2 py-1 rounded">{key || '(not set)'}</span>
          </p>
          <p>
            <span className="text-muted-foreground">In:</span>{' '}
            <Badge variant="outline" className="bg-green-500/10 text-green-600">
              {scopes.find(s => s.value === scope)?.label}
            </Badge>
          </p>
          <p>
            <span className="text-muted-foreground">Expires:</span>{' '}
            <span className="text-xs">{ttl ? ttlOptions.find(o => o.value === ttl)?.label : 'Never'}</span>
          </p>
          <p>
            <span className="text-muted-foreground">Encrypted:</span>{' '}
            <Badge variant={encrypt ? "default" : "outline"} className={encrypt ? "bg-purple-500" : ""}>
              {encrypt ? 'Yes 🔒' : 'No'}
            </Badge>
          </p>
        </CardContent>
      </Card>

      {/* Example */}
      <Card className="bg-muted/50">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Eye className="w-4 h-4" />
            <CardTitle className="text-sm font-medium">Example</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 text-xs">
            <p className="text-muted-foreground">
              <strong>Scenario:</strong> You want to save the total number of customers processed.
            </p>
            <div className="p-2 bg-background rounded border">
              <p><strong>Key:</strong> customer_count</p>
              <p><strong>Value:</strong> {`{{count + 1}}`}</p>
              <p><strong>Scope:</strong> My Data (user)</p>
              <p><strong>Expires:</strong> Never</p>
              <p><strong>Encrypted:</strong> No</p>
            </div>
            <p className="text-muted-foreground">
              This saves the incremented count so you can use it in future workflow runs!
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SetValueConfig;
